import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../../context/AuthContext";
import {
  ConfirmDialog,
  PrimaryButton,
  SecondaryButton,
  TournamentPageSkeleton,
  formatSquadInfo,
} from "../UI/DesignStandards";
import { notify } from "../UI/DesignStandards/Notifications/notify";
import { FaArrowLeft } from "react-icons/fa";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import SignupHeader from "./SignupReview/SignupHeader";
import BowlerCard from "./SignupReview/BowlerCard";
import CostSummary from "./SignupReview/CostSummary";
import ActionButtons from "./SignupReview/ActionButtons";
import AlertBox from "./SignupReview/AlertBox";
import {
  directorCancelSignup,
  directorMoveSignup,
  directorPromoteWaitlist,
  directorSetSignupPayment,
} from "../../utility/signupRpc";
import { refundSignupPayment } from "../../utility/signupPayments";
import { signupPlacement } from "../../utility/signupDashboard";
import { useTournamentId, useTournamentSlug } from "../../context/TournamentRouteContext";
import { tournamentPath } from "../../utility/tournamentUrls";

const SignupReview = () => {
  const { signupId } = useParams();
  const tournamentId = useTournamentId();
  const tournamentSlug = useTournamentSlug();
  const navigate = useNavigate();
  const { session: authSession } = useAuth();
  const [session, setSession] = useState(null);
  const [signup, setSignup] = useState(null);
  const [tournamentName, setTournamentName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRefundStayConfirm, setShowRefundStayConfirm] = useState(false);
  const [showRefundRemoveConfirm, setShowRefundRemoveConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showFifoConfirm, setShowFifoConfirm] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [moveSquadId, setMoveSquadId] = useState("");
  const [moveSquads, setMoveSquads] = useState([]);
  const [bowlerMatches, setBowlerMatches] = useState({});

  const fetchSignupData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      if (!session) {
        setError("You must be logged in to view this page.");
        setLoading(false);
        return;
      }

      const { data, error: fetchError } = await supabase.functions.invoke(
        "get-signup-data",
        {
          body: { signupId: signupId },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (fetchError) {
        console.error("Error fetching signup data:", fetchError);
        setError("Failed to load signup data. Please try again.");
        setLoading(false);
        return;
      }

      if (!data) {
        setError("No signup data found.");
        setLoading(false);
        return;
      }

      const sidepotEvents = data.sidepotEvents || data.sidepots || [];

      const bowlersWithSidepots = (data.bowlers || []).map((bowler) => {
        const bowlerSidepots = (data.existingSidepots || [])
          .filter((sidepot) => sidepot.tournamentSignupBowlerID === bowler.id)
          .map((sidepot) => {
            const sidepotEvent = sidepotEvents.find(
              (event) => event.id === sidepot.eventID
            );
            return {
              eventID: sidepot.eventID,
              eventName: sidepotEvent?.eventName || "Unknown Event",
              entryFee: sidepotEvent?.entryFee || 0,
            };
          });

        const totalSidepotCost = bowlerSidepots.reduce(
          (total, sidepot) => total + (sidepot.entryFee || 0),
          0
        );

        return {
          ...bowler,
          sidepots: bowlerSidepots,
          totalSidepotCost,
        };
      });

      const matches = {};
      for (const bowler of bowlersWithSidepots) {
        if (bowler.usbcID || bowler.email) {
          try {
            const { data: matchData, error: matchError } =
              await supabase.functions.invoke("match-bowler", {
                body: {
                  usbcID: bowler.usbcID,
                  email: bowler.email,
                  tournamentId: tournamentId,
                },
                headers: {
                  Authorization: `Bearer ${session.access_token}`,
                },
              });

            if (
              !matchError &&
              matchData &&
              matchData.matches &&
              matchData.matches.length > 0
            ) {
              matches[bowler.id] = {
                matches: matchData.matches,
                matchType: matchData.matchType,
                searchValue: matchData.searchValue,
                totalMatches: matchData.totalMatches,
              };
            }
          } catch (matchFetchError) {
            console.warn(
              "Error fetching matches for bowler:",
              bowler.name,
              matchFetchError
            );
          }
        }
      }
      setBowlerMatches(matches);

      const { data: refundRequest } = await supabase
        .from("signupRefundRequests")
        .select("id, status")
        .eq("signupID", signupId)
        .eq("status", "open")
        .maybeSingle();

      const flattenedData = {
        ...data.signup,
        bowlers: bowlersWithSidepots,
        sidepots: sidepotEvents,
        existingSidepots: data.existingSidepots,
        signupSettings: data.signupSettings,
        squad: data.squad || null,
        refundRequested: Boolean(refundRequest),
      };
      setSignup(flattenedData);
      setTournamentName(
        data.signup.tournamentEvents?.tournaments?.name || "Unknown"
      );

      setLoading(false);
    } catch (fetchException) {
      console.error("Error fetching signup data:", fetchException);
      setError("Failed to load signup data. Please try again.");
      setLoading(false);
    }
  }, [signupId, tournamentId, session]);

  useEffect(() => {
    const getSession = async () => {
      try {
        const {
          data: { session: supabaseSession },
        } = await supabase.auth.getSession();
        setSession(supabaseSession || authSession);
      } catch (sessionError) {
        console.error("Error getting session:", sessionError);
        setSession(authSession);
      }
    };

    getSession();
  }, [authSession]);

  useEffect(() => {
    if (session) {
      fetchSignupData();
    }
  }, [fetchSignupData, session]);

  const dashboardPath = tournamentPath(tournamentSlug, `/signup-dashboard`);

  const handleMarkPaid = async () => {
    try {
      setActionLoading(true);
      setError(null);
      await directorSetSignupPayment({
        signupId,
        paymentStatus: "walk_up",
      });
      await fetchSignupData({ silent: true });
    } catch (paymentError) {
      setError(paymentError.message || "Could not mark this signup paid.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkUnpaid = async () => {
    try {
      setActionLoading(true);
      setError(null);
      await directorSetSignupPayment({
        signupId,
        paymentStatus: "unpaid",
      });
      await fetchSignupData({ silent: true });
    } catch (paymentError) {
      setError(paymentError.message || "Could not mark this signup unpaid.");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePromote = async () => {
    try {
      setActionLoading(true);
      setError(null);
      await directorPromoteWaitlist({ signupId });
      setShowFifoConfirm(false);
      await fetchSignupData({ silent: true });
    } catch (promoteError) {
      setError(promoteError.message || "Could not put this signup on the squad.");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePromoteClick = async () => {
    const squadId = signup?.squadID || signup?.squad?.id;
    if (!squadId) {
      await handlePromote();
      return;
    }
    const { data, error: waitError } = await supabase
      .from("tournamentSignups")
      .select("id")
      .eq("squadID", squadId)
      .eq("status", "waitlisted")
      .order("created", { ascending: true })
      .limit(1);
    if (waitError) {
      setError(waitError.message);
      return;
    }
    if (data?.[0]?.id && data[0].id !== signupId) {
      setShowFifoConfirm(true);
      return;
    }
    await handlePromote();
  };

  const openMove = async () => {
    const eventId = signup?.eventID || signup?.tournamentEvents?.id;
    setShowMove(true);
    setMoveSquadId("");
    try {
      const { data: squadRows, error: squadError } = await supabase
        .from("squads")
        .select("id, squadDate, squadTime, bowlingCenters(centerName)")
        .eq("tournamentID", tournamentId)
        .eq("isDeleted", false)
        .order("squadDate")
        .order("squadTime");
      if (squadError) throw squadError;
      const { data: extraRows, error: extraError } = await supabase
        .from("squadEvents")
        .select("squadID")
        .eq("eventsID", eventId);
      if (extraError) throw extraError;
      const allowed = new Set((extraRows || []).map((row) => row.squadID));
      const currentId = signup?.squadID || signup?.squad?.id;
      setMoveSquads(
        (squadRows || []).filter(
          (row) => allowed.has(row.id) && row.id !== currentId
        )
      );
    } catch (moveLoadError) {
      setError(moveLoadError.message || "Could not load squads.");
    }
  };

  const handleMove = async () => {
    if (!moveSquadId) return;
    try {
      setActionLoading(true);
      setError(null);
      await directorMoveSignup({
        signupId,
        squadId: moveSquadId,
      });
      setShowMove(false);
      await fetchSignupData({ silent: true });
    } catch (moveError) {
      setError(moveError.message || "Could not move this signup.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefund = async ({ stay }) => {
    try {
      setActionLoading(true);
      setError(null);
      await refundSignupPayment({ signupId, stay });
      setShowRefundStayConfirm(false);
      setShowRefundRemoveConfirm(false);
      if (stay) {
        await fetchSignupData({ silent: true });
        return;
      }
      notify.success("Refunded and removed from squad.");
      navigate(dashboardPath);
    } catch (refundError) {
      setError(refundError.message || "Could not refund this signup.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemove = async () => {
    try {
      setActionLoading(true);
      setError(null);
      await directorCancelSignup({ signupId });
      setShowRemoveConfirm(false);
      notify.success(
        signupPlacement(signup) === "waitlist"
          ? "Removed from waitlist."
          : "Removed from squad."
      );
      navigate(dashboardPath);
    } catch (removeError) {
      setError(removeError.message || "Could not remove this signup.");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading || !session) {
    return (
      <TournamentPageSkeleton
        title="Signup"
        subtitle="Who signed up, what they owe, and waitlist actions."
        cards={3}
      />
    );
  }

  if (error && !signup) {
    return (
      <div className="space-y-4">
        <AlertBox type="error" title="Error" message={error} />
        <SecondaryButton onClick={() => navigate(dashboardPath)}>
          <FaArrowLeft className="w-4 h-4 mr-2" />
          Back to Signup Dashboard
        </SecondaryButton>
      </div>
    );
  }

  if (!signup) {
    return (
      <div className="space-y-4">
        <AlertBox type="error" title="Not Found" message="Signup not found." />
        <SecondaryButton onClick={() => navigate(dashboardPath)}>
          <FaArrowLeft className="w-4 h-4 mr-2" />
          Back to Signup Dashboard
        </SecondaryButton>
      </div>
    );
  }

  const removeIsWaitlist = signupPlacement(signup) === "waitlist";

  return (
    <>
      <div className="flex justify-center mb-3 sm:mb-4 px-4 sm:px-0">
        <Breadcrumb
          items={[
            { label: "Tournaments", path: "/tournaments" },
            {
              label: "Tournament",
              path: tournamentPath(tournamentSlug),
            },
            { label: "Signup Dashboard", path: dashboardPath },
            { label: "Signup" },
          ]}
        />
      </div>

      <div className="space-y-6">
        {error ? (
          <AlertBox type="error" title="Error" message={error} />
        ) : null}

        <SignupHeader signup={signup} tournamentName={tournamentName} />

        <ActionButtons
          signup={signup}
          actionLoading={actionLoading}
          onMarkPaid={handleMarkPaid}
          onMarkUnpaid={handleMarkUnpaid}
          onRemove={() => setShowRemoveConfirm(true)}
          onPromote={handlePromoteClick}
          onMove={openMove}
          onRefundStay={() => setShowRefundStayConfirm(true)}
          onRefundRemove={() => setShowRefundRemoveConfirm(true)}
        />

        <div className="card-board p-6">
          <h3 className="text-lg font-semibold text-brand-charcoal mb-4">
            Bowlers
          </h3>
          <div className="space-y-6">
            {(signup.bowlers || []).map((bowler) => (
              <BowlerCard
                key={bowler.id}
                bowler={bowler}
                matchInfo={bowlerMatches[bowler.id]}
              />
            ))}
          </div>
        </div>

        <CostSummary signup={signup} />
      </div>

      <ConfirmDialog
        isOpen={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        onConfirm={handleRemove}
        title={removeIsWaitlist ? "Remove from waitlist" : "Remove from squad"}
        message={
          removeIsWaitlist
            ? "This removes them from the waitlist. You cannot undo it."
            : "This removes them from the squad. A waitlisted signup may take this spot."
        }
        confirmText="Remove"
        cancelText="Keep signup"
        variant="danger"
        loading={actionLoading}
      />
      <ConfirmDialog
        isOpen={showRefundStayConfirm}
        onClose={() => setShowRefundStayConfirm(false)}
        onConfirm={() => handleRefund({ stay: true })}
        title="Refund and stay"
        message="This refunds the advertised entry and the 2% platform fee. Card processing is not refunded. The signup stays on the squad as Refunded."
        confirmText="Refund"
        cancelText="Keep paid"
        variant="warning"
        loading={actionLoading}
      />
      <ConfirmDialog
        isOpen={showRefundRemoveConfirm}
        onClose={() => setShowRefundRemoveConfirm(false)}
        onConfirm={() => handleRefund({ stay: false })}
        title="Refund and remove"
        message="This refunds the advertised entry and the 2% platform fee, then removes them from the squad. Card processing is not refunded. A waitlisted signup may take this spot."
        confirmText="Refund and remove"
        cancelText="Keep signup"
        variant="danger"
        loading={actionLoading}
      />
      <ConfirmDialog
        isOpen={showFifoConfirm}
        onClose={() => setShowFifoConfirm(false)}
        onConfirm={handlePromote}
        title="This is not the next person in line"
        message="Put this signup on the squad anyway? The waitlist is first come, first served."
        confirmText="Put on squad"
        cancelText="Keep waitlist"
        variant="warning"
        loading={actionLoading}
      />
      {showMove ? (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-medium text-brand-charcoal">
              Move to another squad
            </h3>
            <p className="text-sm text-brand-muted">
              Pick a squad that bowls this event. If that squad is full, they
              join its waitlist.
            </p>
            {moveSquads.length === 0 ? (
              <p className="text-sm text-brand-muted">
                No other squad bowls this event.
              </p>
            ) : (
              <select
                className="w-full p-2 border border-brand-charcoal/20 rounded-md"
                value={moveSquadId}
                onChange={(event) => setMoveSquadId(event.target.value)}
              >
                <option value="">Select a squad</option>
                {moveSquads.map((squad) => (
                  <option key={squad.id} value={squad.id}>
                    {formatSquadInfo(squad.squadDate, squad.squadTime)}
                    {squad.bowlingCenters?.centerName
                      ? ` · ${squad.bowlingCenters.centerName}`
                      : ""}
                  </option>
                ))}
              </select>
            )}
            <div className="flex justify-end gap-3">
              <SecondaryButton
                onClick={() => setShowMove(false)}
                disabled={actionLoading}
              >
                Stay
              </SecondaryButton>
              <PrimaryButton
                onClick={handleMove}
                disabled={actionLoading || !moveSquadId}
              >
                {actionLoading ? "Moving…" : "Move"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default SignupReview;
